import styled from '@emotion/styled';
import React, { FC, useState } from 'react';
import { useStorage } from '~Components/Storage';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useBridgeApi, useButton, useDispatch, useI18n, useLogger } from '~Hooks';
import { BaseInputStyling, BlueButton, Box } from '~Styles';
import { addToast } from '~actions';
import { createLoomisClient } from '~Utils/loomis';

const IpInput = styled.input`
  ${BaseInputStyling}
  padding: 16px 8px;
  margin: 8px 0 16px;
`;

interface Props {
  compact?: boolean;
}

/**
 * The Loomis Pay terminal this device charges LOOMIS payments on, by its IP.
 * Saving asks the terminal whether it answers, so a wrong IP shows up here
 * rather than at the first payment.
 */
const LoomisSetup: FC<Props> = ({ compact = false }) => {
  const { i18n } = useI18n();
  const logger = useLogger('loomis-settings');
  const dispatch = useDispatch();
  const bridgeApi = useBridgeApi();
  const [{ loomisTerminalIp }, updateStorage] = useStorage();
  const [ip, setIp] = useState(loomisTerminalIp ?? '');
  const [checking, setChecking] = useState(false);

  const trimmedIp = ip.trim();

  const saveButton = useButton({
    onClick: async () => {
      logger.info('Setting Loomis terminal IP', { ip: trimmedIp });
      updateStorage({ loomisTerminalIp: trimmedIp || null });

      if (!trimmedIp) {
        return;
      }

      setChecking(true);
      const answers = await createLoomisClient(bridgeApi.fetch, trimmedIp).healthcheck();
      setChecking(false);

      logger.info('Loomis terminal healthcheck', { ip: trimmedIp, answers });
      dispatch(
        addToast(
          i18n(
            answers
              ? 'Settings.Payments.Loomis.Connected'
              : 'Settings.Payments.Loomis.Unreachable',
          ),
          answers,
        ),
      );
    },
    disabled: checking || trimmedIp === (loomisTerminalIp ?? ''),
  });

  return (
    <Box marginTop="large">
      <Heading level={compact ? 3 : 2} color="var(--colorWhite)" bottom={8}>
        {i18n('Settings.Payments.Loomis.Title')}
      </Heading>
      <Paragraph level={2} color="var(--colorBlueGray200)">
        {i18n('Settings.Payments.Loomis.Text')}
      </Paragraph>
      <IpInput
        value={ip}
        inputMode="decimal"
        placeholder="e.g. 192.168.1.127"
        onChange={(e) => setIp(e.target.value)}
      />
      <BlueButton {...saveButton}>{i18n('Settings.Save')}</BlueButton>
    </Box>
  );
};

export default LoomisSetup;
